<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import {
  GpxMap,
  GpxMapBasemapToggle,
  GpxMapFitBoundsControl,
  GpxMapZoomControl,
  useMapInteractions,
} from '@gpx-spa/ui';
import { createLeafletMapProvider } from '@gpx-spa/geo-adapters';
import {
  geofenceBounds,
  geofencesToGeoJSON,
  htmlSurface,
  isMarkerHit,
  latLngBoundsFromPoints,
} from '@gpx-spa/geo-domain';
import type {
  Geofence,
  LatLngBounds,
  MapBasemapMode,
  MapInteractionsConfig,
  MapProvider,
} from '@gpx-spa/geo-domain';
import type {
  FindContainersSite,
  FindContainersTracker,
} from '@gpx-spa/queries/src/getAccountFindContainers';
import {
  APP_MAP_SATELLITE_TILE_URL,
  APP_MAP_TILE_OPTIONS,
  APP_MAP_TILE_URL,
  getAppMapTileAttribution,
  isMapAttributionVisible,
} from '@/utils/app-map-tiles';
import { COMPOSER_MAP_DEFAULT_CENTER } from '@/utils/shipment-map-constants';
import { afterNextBrowserPaint } from '@/utils/after-next-browser-paint';
import {
  searchRadiusGeofence,
  siteGeofence,
  siteMarkerId,
  siteMarkers,
  siteTooltipHtml,
  trackerMarkerId,
  SITE_TOOLTIP_OFFSET,
  TRACKER_TOOLTIP_OFFSET,
  trackerMarkers,
  trackerTooltipHtml,
} from '@/utils/find-containers';
import * as events from '@/events';

const LAYER_SEARCH_RADIUS_ID = 'find-containers/search-radius';
const LAYER_SITES_ID = 'find-containers/sites';
const LAYER_DESTINATION_ID = 'find-containers/destination';
const LAYER_SITE_MARKERS_ID = 'find-containers/site-markers';
const LAYER_TRACKERS_ID = 'find-containers/trackers';

const TRACKER_MIN_ZOOM = 12;

const OUTLINE_COLOR: Record<MapBasemapMode, string> = {
  satellite: '#ffffff',
  streets: '#1d2e30',
};

function siteStyle(mode: MapBasemapMode) {
  const color = OUTLINE_COLOR[mode];
  return {
    fill: color,
    fillOpacity: 0.12,
    stroke: color,
    strokeOpacity: 0.9,
    strokeWidth: 2,
  };
}

function searchRadiusStyle(mode: MapBasemapMode) {
  const color = OUTLINE_COLOR[mode];
  return {
    fill: color,
    fillOpacity: 0.05,
    stroke: color,
    strokeOpacity: 0.6,
    strokeWidth: 2,
  };
}

const props = defineProps<{
  sites: FindContainersSite[];
  trackers: FindContainersTracker[];
  destination: { lat: number; lng: number; name: string } | null;
  searchRadiusMeters: number | null;
}>();

const emit = defineEmits<{
  selectSite: [site: FindContainersSite];
}>();

const basemapMode = ref<MapBasemapMode>('satellite');
const mapReady = ref(false);

watch(basemapMode, (to) => {
  events.trigger(events.names.FIND_CONTAINERS_BASEMAP_TOGGLED, { to });
});

const mapProvider: MapProvider = createLeafletMapProvider({
  basemap: {
    streets: { rasterUrl: APP_MAP_TILE_URL },
    satellite: { rasterUrl: APP_MAP_SATELLITE_TILE_URL },
    defaultMode: basemapMode.value,
  },
  getTileAttribution: getAppMapTileAttribution,
  tileLayerOptions: { ...APP_MAP_TILE_OPTIONS },
  defaultCenter: COMPOSER_MAP_DEFAULT_CENTER,
  defaultZoom: 4,
  showAttribution: isMapAttributionVisible(),
  zoomControl: false,
});

/**
 * Popups rather than tooltips, as on the trackers page: they stay open when the pointer leaves and close
 * when another one opens, or when their marker is repainted (see paintZoomDependentLayers).
 */
function hoverPopup(html: string, offset: [number, number]) {
  return htmlSurface(html, {
    presentation: 'popup',
    anchor: { offset },
    dismiss: { closeButton: false, closeOnMapClick: false },
  });
}

const hoverPopups = computed(
  () =>
    new Map([
      ...props.sites.map(
        (s) =>
          [
            siteMarkerId(s),
            hoverPopup(siteTooltipHtml(s), SITE_TOOLTIP_OFFSET),
          ] as const,
      ),
      ...props.trackers.map(
        (t) =>
          [
            trackerMarkerId(t),
            hoverPopup(trackerTooltipHtml(t), TRACKER_TOOLTIP_OFFSET),
          ] as const,
      ),
    ]),
);

const sitesByMarkerId = computed(
  () => new Map(props.sites.map((s) => [siteMarkerId(s), s])),
);

const interactionConfig = computed<MapInteractionsConfig>(() => ({
  triggers: ['click', 'hover'],
  onHit: (event) => {
    if (event.trigger !== 'click' || !isMarkerHit(event)) return;
    const site = sitesByMarkerId.value.get(event.target.marker.id);
    if (site) emit('selectSite', site);
  },
  surface: (event) => {
    if (event.trigger !== 'hover' || !isMarkerHit(event)) return null;
    return hoverPopups.value.get(event.target.marker.id) ?? null;
  },
}));

const { reapply: reapplyMapInteractions } = useMapInteractions(
  mapProvider,
  interactionConfig,
);

const siteGeofences = computed(() =>
  props.sites.map(siteGeofence).filter((g): g is Geofence => g !== null),
);

const searchRadius = computed(() =>
  searchRadiusGeofence(props.destination, props.searchRadiusMeters),
);

const contentBounds = computed<LatLngBounds | null>(() => {
  if (searchRadius.value) return geofenceBounds(searchRadius.value);
  const points = [
    ...siteGeofences.value.flatMap((g) => {
      const b = geofenceBounds(g);
      return b ? [b.southWest, b.northEast] : [];
    }),
    ...(props.destination ? [props.destination] : []),
  ];
  return latLngBoundsFromPoints(points);
});

function paintLayers() {
  if (!mapReady.value) return;

  mapProvider.addLayer(LAYER_SEARCH_RADIUS_ID).setGeoJSON(
    geofencesToGeoJSON(searchRadius.value ? [searchRadius.value] : [], {
      map: searchRadiusStyle(basemapMode.value),
    }),
  );

  mapProvider.addLayer(LAYER_SITES_ID).setGeoJSON(
    geofencesToGeoJSON(siteGeofences.value, {
      map: siteStyle(basemapMode.value),
    }),
  );

  const destinationLayer = mapProvider.addLayer(LAYER_DESTINATION_ID);
  if (props.destination) {
    destinationLayer.setMarkers([
      {
        id: 'destination',
        position: props.destination,
        style: {
          kind: 'circle',
          fillColor: '#1d2e30',
          borderColor: '#ffffff',
          sizePx: 18,
        },
      },
    ]);
  } else {
    destinationLayer.clear();
  }

  paintZoomDependentLayers({ force: true });
}

let paintedZoomedIn: boolean | null = null;

/** Repainting closes the open popup, so a pan or zoom that keeps the same markers leaves them alone. */
function paintZoomDependentLayers({ force = false } = {}) {
  if (!mapReady.value) return;
  const zoomedIn = (mapProvider.getZoom() ?? 0) >= TRACKER_MIN_ZOOM;
  if (!force && zoomedIn === paintedZoomedIn) return;
  paintedZoomedIn = zoomedIn;
  mapProvider
    .addLayer(LAYER_SITE_MARKERS_ID)
    .setMarkers(zoomedIn ? [] : siteMarkers(props.sites));
  mapProvider
    .addLayer(LAYER_TRACKERS_ID)
    .setMarkers(zoomedIn ? trackerMarkers(props.trackers) : []);
  reapplyMapInteractions();
}

function paintAndFit() {
  void afterNextBrowserPaint().then(() => {
    paintLayers();
    if (contentBounds.value) mapProvider.fitBounds(contentBounds.value);
  });
}

watch(
  () => [
    props.sites,
    props.trackers,
    props.destination,
    props.searchRadiusMeters,
    basemapMode.value,
  ],
  () => {
    void afterNextBrowserPaint().then(paintLayers);
  },
);

watch(
  () => JSON.stringify(contentBounds.value),
  () => {
    if (contentBounds.value) mapProvider.fitBounds(contentBounds.value);
  },
);

let stopTrackingMoves: (() => void) | undefined;

function onMapReady() {
  mapReady.value = true;
  stopTrackingMoves = mapProvider.onMoveEnd(() => paintZoomDependentLayers());
  paintAndFit();
}

onBeforeUnmount(() => stopTrackingMoves?.());

function fitToSite(site: FindContainersSite) {
  const geofence = siteGeofence(site);
  const bounds = geofence && geofenceBounds(geofence);
  if (bounds) mapProvider.fitBounds(bounds);
}

function fitToContent() {
  if (contentBounds.value) mapProvider.fitBounds(contentBounds.value);
}

defineExpose({ fitToSite, fitToContent });
</script>

<template>
  <div class="relative size-full min-h-0">
    <GpxMap
      class="size-full min-h-0"
      :map-provider="mapProvider"
      @ready="onMapReady"
    >
      <template #top-right>
        <GpxMapBasemapToggle v-model="basemapMode" />
      </template>
      <template #right-center>
        <div class="flex flex-col items-end gap-1">
          <GpxMapZoomControl :max-zoom="20" />
          <GpxMapFitBoundsControl
            v-if="contentBounds"
            :bounds="contentBounds"
            button-title="Fit all map content"
            aria-label="Fit all map content"
          />
        </div>
      </template>
    </GpxMap>
  </div>
</template>
